import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from './Modal';
import Button from './Button';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger', // 'danger', 'warning', 'info'
  loading = false
}) => {
  const { isMobile, getResponsiveValue } = useTheme();

  const getIcon = () => {
    const iconSize = getResponsiveValue({
      mobile: 'w-10 h-10',
      desktop: 'w-12 h-12'
    });

    const innerIconSize = getResponsiveValue({
      mobile: 'w-5 h-5',
      desktop: 'w-6 h-6'
    });

    switch (type) {
      case 'danger':
        return (
          <div className={`${iconSize} bg-error-subtle rounded-xl flex items-center justify-center`}>
            <svg className={`${innerIconSize} text-error`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.08 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className={`${iconSize} bg-warning-subtle rounded-xl flex items-center justify-center`}>
            <svg className={`${innerIconSize} text-warning`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.08 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'info':
      default:
        return (
          <div className={`${iconSize} bg-info-subtle rounded-xl flex items-center justify-center`}>
            <svg className={`${innerIconSize} text-info`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getConfirmButtonVariant = () => {
    switch (type) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'secondary';
      case 'info':
      default:
        return 'primary';
    }
  };

  // Responsive configurations
  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    tablet: 'p-5',
    desktop: 'p-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-lg',
    desktop: 'text-lg'
  });

  const messageSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'md',
    desktop: 'md'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'space-x-2',
    desktop: 'space-x-3'
  });

  const sectionSpacing = getResponsiveValue({
    mobile: 'space-y-4',
    desktop: 'space-y-6'
  });

  const contentSpacing = getResponsiveValue({
    mobile: 'space-y-2',
    desktop: 'space-y-3'
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      closeOnOverlay={!loading}
      maxHeight="small"
    >
      <div className={`${containerPadding} text-center ${sectionSpacing}`}>
        {/* Icon */}
        <div className="flex justify-center">
          {getIcon()}
        </div>

        {/* Content */}
        <div className={contentSpacing}>
          <h3 className={`${titleSize} font-semibold text-text-primary`}>
            {title}
          </h3>
          <p className={`text-text-secondary ${messageSize} leading-relaxed ${isMobile ? 'break-words' : ''}`}>
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className={`flex ${isMobile ? 'flex-col-reverse space-y-reverse space-y-2' : `${buttonSpacing} justify-center`} pt-2`}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            size={buttonSize}
            fullWidth={isMobile}
          >
            {cancelText}
          </Button>
          <Button
            variant={getConfirmButtonVariant()}
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
            size={buttonSize}
            fullWidth={isMobile}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;